import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";

import Modal from "../../../components/Reusable/Modal";
import RichTextEditor from "../../../components/Reusable/RichTextEditor";
import {
  fetchSupport,
  createSupport,
  updateSupport,
  deleteSupport,
} from "../../../features/auth/store/supportSlice";
import { SupportItem } from "../../../features/auth/store/types/support-types";

interface SupportManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialEditItem?: SupportItem | null;
}

const SupportManagementModal: React.FC<SupportManagementModalProps> = ({
  isOpen,
  onClose,
  initialEditItem,
}) => {
  const dispatch = useAppDispatch();
  const items = useAppSelector((state: any) => state.support?.items ?? []);
  const loading = useAppSelector((state: any) => state.support?.loading ?? false);
  const saving = useAppSelector((state: any) => state.support?.saving ?? false);

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [editingItem, setEditingItem] = useState<SupportItem | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const hasVisibleText = (html: string): boolean => {
    const stripped = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    return stripped.length > 0;
  };

  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setEditingItem(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    dispatch(fetchSupport());
    if (initialEditItem) {
      setEditingItem(initialEditItem);
      setQuestion(initialEditItem.question);
      setAnswer(initialEditItem.answer);
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialEditItem]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!question.trim() || !hasVisibleText(answer)) {
      toast.error("Question and answer are required");
      return;
    }

    try {
      if (editingItem) {
        await dispatch(
          updateSupport({ id: editingItem.id, question, answer })
        ).unwrap();
        toast.success("Support item updated successfully");
      } else {
        await dispatch(createSupport({ question, answer })).unwrap();
        toast.success("Support item created successfully");
      }
      resetForm();
    } catch (err: any) {
      toast.error(err || "Failed to save support item");
    }
  };

  const handleEdit = (item: SupportItem) => {
    setEditingItem(item);
    setQuestion(item.question);
    setAnswer(item.answer);
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await dispatch(deleteSupport(id)).unwrap();
      toast.success("Support item deleted successfully");
      if (editingItem?.id === id) resetForm();
    } catch (err: any) {
      toast.error(err || "Failed to delete support item");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-2xl">
      <h2 className="text-xl font-bold mb-4">Manage Support</h2>

      <form onSubmit={handleSubmit} className="mb-6">
        <div className="mb-4">
          <label className="block mb-2">Question</label>
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            required
            className="w-full p-2 border rounded dark:bg-white/10 dark:text-white"
          />
        </div>

        <div className="mb-4">
          <label className="block mb-2">Answer</label>
          <RichTextEditor
            value={answer}
            onChange={setAnswer}
            placeholder="Write answer..."
          />
        </div>

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-gradient-to-r from-[#027EC2] to-[#039EF2] text-white rounded-lg disabled:opacity-70"
          >
            {saving ? "Saving..." : editingItem ? "Update" : "Add"}
          </button>
          {editingItem && (
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-lg"
            >
              Cancel Edit
            </button>
          )}
        </div>
      </form>

      <div className="border border-[#D0D5DD] dark:border-[#FFFFFF1A] rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-gray-100 dark:bg-[#FFFFFF1A] font-semibold">
          Support Items
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No support items yet.
            </div>
          ) : (
            items.map((item: SupportItem) => (
              <div
                key={item.id}
                className="p-4 border-t border-[#D0D5DD] dark:border-[#FFFFFF1A]"
              >
                <p className="font-semibold mb-1">{item.question}</p>
                <div
                  className="text-sm text-gray-600 dark:text-gray-300 prose prose-sm max-w-none dark:prose-invert [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:no-underline hover:[&_a]:text-blue-700 dark:hover:[&_a]:text-blue-300"
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="text-red-600 text-sm disabled:opacity-70"
                  >
                    {deletingId === item.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SupportManagementModal;
